import * as React from "react";
import { Box, Button } from "@mui/material";
import IComponentProps from "./interface";
import { FormProvider, useForm } from "react-hook-form";
import { t, Trans } from "@lingui/macro";
import Grid from "@mui/material/Grid";
import "flatpickr/dist/themes/material_green.css";
import ButtonAtom from "@atoms/Button";
import RadioGroupMoleculeA from "@molecules/RadioGroup";
import { useSelector } from "react-redux";
import { IType } from "@interfaces/IType";
import RatesService from "@services/RatesService";
import HelperErrorMolecule from "@molecules/HelperError";
import ClienSupplierRatesForm from "./Form";
import DataBalloonAtom from "@atoms/DataBalloon";
import SpinnerAtom from "@atoms/Spinner";
import { useAppDispatch } from "@stores/hooks";
import { addForm, updateForm } from "@stores/reducers/saverReducer";
import { ratesFeaturesEnabled } from "@utils/features";

const InspectionRatesForm = ({
  inspection,
  clientRecord,
  isInd,
  supplierRecord,
  onBack,
  onNext,
  onError,
}: IComponentProps) => {
  const [sErrors /*, setSErrors*/] = React.useState<any>({}); // inutile???
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [clientDirty, setClientDirty] = React.useState<boolean>(false);
  const [supplierDirty, setSupplierDirty] = React.useState<boolean>(false);
  const [clientData, setClientData] = React.useState<any>(clientRecord ?? {});
  const [errors, setErrors] = React.useState<any>({});
  // Bumped after a successful save to force both forms to re-sync from the
  // server response (the id-guarded reset inside the form won't fire on its own
  // when the record id stays the same).
  const [refreshToken, setRefreshToken] = React.useState<number>(0);
  const [supplierData, setSupplierData] = React.useState<any>(
    supplierRecord ?? {},
  );
  const ratesTypesList = useSelector(
    (state: any) => state.filters.filters.types?.rates,
  );
  const currencyList = useSelector(
    (state: any) => state.filters.filters.categories?.rates,
  );
  const autoSave = useSelector((state: any) => state.saver.saveAll);
  const dispatch = useAppDispatch();

  const formInstance = useForm<any>({
    mode: "onChange",
    reValidateMode: "onSubmit",
  });

  const {
    handleSubmit: handleHookSubmit,
    formState,
    watch,
    setValue,
    reset,
  } = formInstance;

  const isCalculated = watch("is_calculated");
  const type = watch("type_id");
  const isHours = watch("time_unit") === "hours";
  const isDays = watch("time_unit") === "days";

  React.useEffect(() => {
    if (clientRecord && clientRecord.id) {
      setClientData(clientRecord);
    }
  }, [clientRecord]);

  React.useEffect(() => {
    if (supplierRecord && supplierRecord.id) {
      setSupplierData(supplierRecord);
    }
  }, [supplierRecord]);

  // Default rates of the assigned (internal) inspector. These come from the
  // inspection's eager-loaded `user` relation (backend Inspection $with selects
  // internal + default_daily_rate + default_hourly_rate). The global users
  // filter (UserFilterResource) does NOT expose those fields, so it cannot be
  // used here. Used to pre-populate the supplier rate — pre-population only,
  // the field stays fully editable afterwards.
  const inspectorDefaults = React.useMemo(() => {
    // Hidden together with the other rates features: no prefill from defaults.
    if (!ratesFeaturesEnabled()) return null;
    const inspector = inspection?.user;
    if (!inspector || !inspector.internal) return null;
    const norm = (v: any) =>
      v === "" || v === undefined || v === null ? null : v;
    const daily = norm(inspector.default_daily_rate);
    const hourly = norm(inspector.default_hourly_rate);
    if (daily === null && hourly === null) return null;
    return { daily, hourly };
  }, [inspection]);

  const supplierSeeded = React.useRef(false);
  React.useEffect(() => {
    if (supplierSeeded.current) return;
    if (!inspectorDefaults) return;
    // Never override an already-persisted or already-filled supplier rate.
    if (supplierData?.id) return;
    if (supplierData?.info?.rate) return;
    if (!ratesTypesList || !currencyList) return;

    supplierSeeded.current = true;
    const hasDaily = inspectorDefaults.daily !== null;
    const initialUnit = hasDaily ? "days" : "hours";
    const initialRate = hasDaily
      ? inspectorDefaults.daily
      : inspectorDefaults.hourly;
    const actCount = inspection?.total_activities ?? 0;

    setSupplierData((prev: any) => ({
      ...prev,
      custom: false,
      type_id: ratesTypesList[1].id,
      category_id: prev?.category_id ?? currencyList[0].id,
      info: {
        ...(prev?.info ?? {}),
        is_calculated: true,
        time_unit: initialUnit,
        rate: initialRate,
        // custom is off by default, so the day count mirrors the activities.
        ...(hasDaily ? { days: actCount } : {}),
      },
    }));
  }, [inspectorDefaults, supplierData, ratesTypesList, currencyList]);

  React.useEffect(() => {
    if (autoSave.status && (supplierDirty || clientDirty)) {
      handleSubmit();
    }
  }, [autoSave]);

  React.useEffect(() => {
    reset({
      category_id: currencyList ? currencyList[0].id : "",
      type_id: ratesTypesList ? ratesTypesList[0].id : "",
      is_calculated: false,
      time_unit: "days",
      allowance_quantity: "",
      allowance: "",
      value: "",
      rate: "",
      days: "",
      extra: "",
    });
  }, [ratesTypesList, currencyList]);

  React.useEffect(() => {
    if (!isDays) {
      setValue("days", "");
      setValue("allowance_quantity", "");
      setValue("allowance", "");
    }
    if (!isHours) {
      setValue("hours", "");
    }
    if (!isCalculated) {
      setValue("days", "");
      setValue("hours", "");
      setValue("rate", "");
      setValue("allowance_quantity", "");
      setValue("allowance", "");
    }
  }, [isHours, isDays, isCalculated, setValue]);

  const mutate = (data: any) => {
    // Ensure info is stringified for API
    const payload = {
      ...data,
      info: typeof data.info === 'object' ? JSON.stringify(data.info) : data.info,
      model_id: inspection?.id,
      // With the rates features hidden the Custom toggle is not shown: days are
      // always manual, so never let the backend auto-sync them.
      ...(ratesFeaturesEnabled() ? {} : { custom: true }),
    };
    return data.id
      ? RatesService.put(payload, data.id)
      : RatesService.create(payload);
  };

  const handleSubmit = async () => {
    setErrors({});
    let hasErrors = false;
    setIsLoading(true);
    let tmpErrors = {};
    // Save client
    if (/*Number(type) === */ ratesTypesList[0].id) {
      const responseA: any = await mutate(clientData);
      if (!responseA.hasErrors()) {
        setClientData(responseA.getData());
      } else {
        tmpErrors = { clientErrors: responseA.getErrors() };
        hasErrors = true;
      }
    }
    // Save supplier
    if (/*Number(type) === */ ratesTypesList[1].id) {
      const responseB: any = await mutate(supplierData);
      if (!responseB.hasErrors()) {
        setSupplierData(responseB.getData());
      } else {
        tmpErrors = {
          ...tmpErrors,
          ...{ supplierErrors: responseB.getErrors() },
        };
        hasErrors = true;
      }
    }
    setErrors(tmpErrors);
    setIsLoading(false);
    if (!hasErrors) {
      // Force both forms to refresh their fields from the saved server data.
      setRefreshToken((t) => t + 1);
      dispatch(
        updateForm({
          slug: "inspection-detail-rates",
          dirty: false,
          saved: true,
        }),
      );
      if (onNext) {
        onNext();
      }
    } else {
      if (onError) {
        onError();
      }
    }
  };

  const clientChanged = (data: any) => {
    const inData: any = {};
    inData.id = data.id;
    const actCount = inspection?.total_activities ?? 0;
    const syncedDays = !data.custom && data.is_calculated && data.time_unit === 'days' ? actCount : data.days;
    const syncedValue = !data.custom && data.is_calculated && data.time_unit === 'days'
      ? Number(data.rate) * actCount + Number(data.extra ?? 0)
      : data.value;
    
    // Keep info as object if clientData already has it as object (from API)
    // Only stringify when preparing for API submission
    const infoData = {
      days: syncedDays,
      hours: data.hours,
      rate: data.rate,
      allowance_quantity: data.allowance_quantity,
      allowance: data.allowance,
      extra: data.extra,
      is_calculated: data.is_calculated,
      time_unit: data.time_unit,
    };
    
    inData.info = clientData.info && typeof clientData.info === 'object' 
      ? infoData 
      : JSON.stringify(infoData);
    
    inData.value = syncedValue;
    inData.category_id = data.category_id;
    inData.type_id = data.type_id;
    inData.custom = data.custom;  // custom is top-level, not in info
    inData.model_type = "inspections";
    setClientData({ ...clientData, ...inData });
  };

  const supplierChanged = (data: any) => {
    const inData: any = {};
    inData.id = data.id;
    const actCount = inspection?.total_activities ?? 0;
    const syncedDays = !data.custom && data.is_calculated && data.time_unit === 'days' ? actCount : data.days;
    const syncedValue = !data.custom && data.is_calculated && data.time_unit === 'days'
      ? Number(data.rate) * actCount + Number(data.extra ?? 0)
      : data.value;
    
    // Keep info as object if supplierData already has it as object (from API)
    // Only stringify when preparing for API submission
    const infoData = {
      days: syncedDays,
      hours: data.hours,
      rate: data.rate,
      allowance_quantity: data.allowance_quantity,
      allowance: data.allowance,
      extra: data.extra,
      is_calculated: data.is_calculated,
      time_unit: data.time_unit,
    };
    
    inData.info = supplierData.info && typeof supplierData.info === 'object' 
      ? infoData 
      : JSON.stringify(infoData);
    
    inData.value = syncedValue;
    inData.category_id = data.category_id;
    inData.type_id = data.type_id;
    inData.custom = data.custom;  // custom is top-level, not in info
    inData.model_type = "inspections";
    setSupplierData({ ...supplierData, ...inData });
  };

  const handleOnDirtyChangeClient = (isDirty: boolean) => {
    setClientDirty(isDirty);
  };

  const handleOnDirtyChangeSupplier = (isDirty: boolean) => {
    setSupplierDirty(isDirty);
  };

  React.useEffect(() => {
    if (supplierDirty || clientDirty) {
      dispatch(
        addForm({
          slug: "inspection-detail-rates",
          formState: formInstance.formState,
        }),
      );
    }
  }, [supplierDirty, clientDirty]);

  return (
    <Box>
      {!ratesTypesList || !currencyList ? (
        <SpinnerAtom />
      ) : (
        <>
          <form noValidate onSubmit={handleHookSubmit(handleSubmit)}>
            <FormProvider {...formInstance}>
              <Box mb={2}>
                <Grid container spacing={1}>
                  <Grid item md={7} xs={12}>
                    <RadioGroupMoleculeA
                      control={formInstance.control}
                      controlName="type_id"
                      radios={
                        ratesTypesList
                          ? ratesTypesList.map((type: IType) => {
                              return {
                                name: (
                                  <div
                                    style={{
                                      display: "flex",
                                      justifyContent: "space-around",
                                      width: "100%",
                                      alignItems: "center",
                                    }}
                                  >
                                    {type.name}
                                    &nbsp;
                                    {((type.name.toLowerCase() === "client" &&
                                      errors.clientErrors) ||
                                      (type.name.toLowerCase() === "supplier" &&
                                        errors.supplierErrors)) && (
                                      <DataBalloonAtom
                                        size={"small"}
                                        severity="error"
                                        data={
                                          type.name.toLowerCase() ===
                                            "client" && errors.clientErrors
                                            ? Object.keys(errors.clientErrors)
                                                .length
                                            : type.name.toLowerCase() ===
                                                "supplier" &&
                                              errors.supplierErrors
                                            ? Object.keys(errors.supplierErrors)
                                                .length
                                            : 0
                                        }
                                      />
                                    )}
                                  </div>
                                ),
                                id: Number(type.id),
                              };
                            })
                          : null
                      }
                    />
                    <HelperErrorMolecule message={sErrors["type_id"]} />
                  </Grid>
                </Grid>
              </Box>
            </FormProvider>
          </form>

          <Box>
            {ratesTypesList && (
              <React.Fragment>
                {
                  <Box
                    sx={{
                      display:
                        Number(type) === ratesTypesList[0].id
                          ? "block"
                          : "none",
                    }}
                  >
                    <ClienSupplierRatesForm
                      onDirtyChange={handleOnDirtyChangeClient}
                      record={clientData}
                      onChange={clientChanged}
                      errors={errors.clientErrors}
                      model={inspection}
                      typeId={ratesTypesList[0].id}
                      syncToken={refreshToken}
                    />
                  </Box>
                }
                {
                  <Box
                    sx={{
                      display:
                        Number(type) === ratesTypesList[1].id
                          ? "block"
                          : "none",
                    }}
                  >
                    <ClienSupplierRatesForm
                      onDirtyChange={handleOnDirtyChangeSupplier}
                      record={supplierData}
                      onChange={supplierChanged}
                      errors={errors.supplierErrors}
                      model={inspection}
                      typeId={ratesTypesList[1].id}
                      syncToken={refreshToken}
                      defaultDailyRate={inspectorDefaults?.daily ?? null}
                      defaultHourlyRate={inspectorDefaults?.hourly ?? null}
                    />
                  </Box>
                }
              </React.Fragment>
            )}
          </Box>
          <Box sx={{ display: "flex", flexDirection: "row", pt: 2 }}>
            {!isInd && (
              <Button color="inherit" onClick={onBack} sx={{ mr: 1 }}>
                <Trans>Back</Trans>
              </Button>
            )}
            <Box sx={{ flex: "1 1 auto" }} />
            <ButtonAtom
              variant="contained"
              loading={isLoading}
              label={!isInd ? t`Complete` : t`Save`}
              onClick={() => handleSubmit()}
              disabled={!formState.isValid}
              sx={{ mr: 1 }}
            />
          </Box>
        </>
      )}
    </Box>
  );
};

export default InspectionRatesForm;
